// src/types/dnl.ts
/**
 * Double Number Line (DNL) Enhancement Types
 * Phase 1: Custom/Sparse Points System
 * 
 * Based on DNL Enhancement Specification v1.0
 */

import type { Fraction } from './redux';

// =============================================================================
// CUSTOM TICK ENHANCEMENTS FOR DNL
// =============================================================================

/**
 * Enhanced CustomTick for DNL functionality
 * Extends the basic CustomTick with line assignment and styling
 */
export interface DNLCustomTick {
  id: string;
  /** Numeric value on the number line */
  value: number;
  /** Optional custom label text (e.g., "100g", "?") */
  label?: string;
  /** Which line this tick belongs to */
  line: 'primary' | 'secondary' | 'both';
  /** Whether to show the tick mark */
  showTick: boolean;
  /** Whether to show the label */
  showLabel: boolean;
  /** Visual styling */
  style: 'normal' | 'highlighted' | 'question';
  /** Optional custom colour */
  colour?: string;
  /** Timestamp for ordering */
  createdAt: number;
  /** Optional link to corresponding tick on other line */
  linkedTickId?: string;
}

// =============================================================================
// SPARSE TICK MODE
// =============================================================================

/**
 * Configuration for sparse tick mode
 * When enabled, hides regular auto-generated ticks and shows only custom points
 */
export interface SparseTickMode {
  /** Whether sparse mode is enabled */
  enabled: boolean;
  /** Hide auto-generated ticks when enabled */
  hideRegularTicks: boolean;
  /** Custom ticks to display */
  customTicks: DNLCustomTick[];
  /** Always show zero (enforced for DNL) */
  showZero: boolean;
  /** Next ID for new custom ticks */
  nextId: number;
}

// =============================================================================
// LINE LABELS
// =============================================================================

/**
 * Configuration for contextual line labels
 * e.g., "People", "ml sauce"
 */
export interface LineLabel {
  /** Main label text */
  text: string;
  /** Optional subtext (e.g., unit) */
  subtext?: string;
  /** Position relative to line */
  position: 'left' | 'right';
  /** Whether the label is visible */
  visible: boolean;
}

// =============================================================================
// UNIT SUFFIX
// =============================================================================

/**
 * Configuration for unit suffixes on labels
 * Auto-appended to all labels on a line (e.g., "g", "ml")
 */
export interface UnitSuffix {
  /** The unit text */
  text: string;
  /** Position relative to the number */
  position: 'before' | 'after';
  /** Whether to show on labels */
  enabled: boolean;
}

// =============================================================================
// MAPPING ARROWS
// =============================================================================

/**
 * Configuration for a single mapping arrow connecting primary and secondary lines
 */
export interface MappingArrow {
  id: string;
  /** Position on primary line where arrow connects */
  value: number;
  /** Whether this arrow is visible */
  visible: boolean;
  /** Line style */
  style: 'solid' | 'dashed' | 'dotted';
  /** Which arrowheads to show */
  showArrowHeads: 'none' | 'top' | 'bottom' | 'both';
  /** Show the scale factor multiplier label (×k) */
  showMultiplierLabel: boolean;
  /** Arrow color */
  colour: string;
}

/**
 * Configuration for mapping arrows feature
 */
export interface MappingArrowsConfig {
  /** Whether mapping arrows feature is enabled */
  enabled: boolean;
  /** Array of mapping arrows */
  arrows: MappingArrow[];
  /** Default style for new arrows */
  defaultStyle: 'solid' | 'dashed' | 'dotted';
  /** Show multiplier labels by default */
  showMultiplierLabels: boolean;
  /** Default arrow color */
  defaultColour: string;
}

// =============================================================================
// DNL TOOLS STATE
// =============================================================================

// =============================================================================
// PHASE 5: ALTERNATIVE VIEWS CONFIGURATION
// =============================================================================

/**
 * Represents a stored point configuration for a specific view
 * Stores the value and which line it belongs to
 */
export interface StoredViewPoint {
  /** Unique identifier matching the DNLCustomTick.id */
  id: string;
  /** The value on the number line */
  value: number;
  /** Custom label text */
  label?: string;
  /** Which line this point belongs to in this view */
  line: 'primary' | 'secondary';
  /** Visual styling */
  style: 'normal' | 'highlighted' | 'question';
  /** Optional custom colour */
  colour?: string;
}

/**
 * Configuration for one view mode (A or B)
 * Stores the labels and point assignments for that view
 */
export interface ViewConfiguration {
  /** Label for primary line in this view */
  primaryLabel: string;
  /** Optional subtext for primary label */
  primarySubtext?: string;
  /** Label for secondary line in this view */
  secondaryLabel: string;
  /** Optional subtext for secondary label */
  secondarySubtext?: string;
  /** Points configuration for this view */
  points: StoredViewPoint[];
}

/**
 * Complete Alternative Views state
 * Phase 5: Alternative Views Feature
 */
export interface DNLAlternativeViews {
  /** Current active view mode */
  currentView: 'A' | 'B';
  /** Configuration for View A (Entity view) */
  viewA: ViewConfiguration;
  /** Configuration for View B (Measure view) */
  viewB: ViewConfiguration;
  /** Whether animations are enabled for view transitions */
  animationsEnabled: boolean;
  /** Whether to show the pedagogical tooltip */
  showTooltip: boolean;
  /** Whether views have been configured (at least once) */
  isConfigured: boolean;
}

// =============================================================================
// PHASE 6: CONTEXT PRESETS
// =============================================================================

/**
 * Preset identifier for common DNL scenarios
 */
export type DNLContextPreset = 
  | 'recipe'
  | 'metric-length'
  | 'percentage'
  | 'fraction-decimal'
  | 'enlargement'
  | 'currency'
  | 'custom';

/**
 * Configuration for a context preset
 * Defines default values for labels, formats, and scale factors
 */
export interface PresetConfiguration {
  /** Human-readable name */
  name: string;
  /** Description for tooltip */
  description: string;
  /** Primary line label */
  primaryLabel: string;
  /** Primary line subtext */
  primarySubtext?: string;
  /** Secondary line label */
  secondaryLabel: string;
  /** Secondary line subtext */
  secondarySubtext?: string;
  /** Primary line number format */
  primaryFormat: import('./redux').NumberFormatConfig;
  /** Secondary line number format */
  secondaryFormat: import('./redux').NumberFormatConfig;
  /** Suggested scale factor (null = user must specify) */
  suggestedScaleFactor?: { numerator: number; denominator: number };
  /** Icon name for UI display */
  icon?: string;
}

/**
 * Context preset state
 */
export interface ContextPresetState {
  /** Currently selected preset */
  selectedPreset: DNLContextPreset;
  /** Whether a preset is currently applied (false = custom configuration) */
  isPresetApplied: boolean;
  /** Whether to show preset descriptions in UI */
  showDescriptions: boolean;
}

// =============================================================================
// PHASE 7: RATIO TABLE OVERLAY
// =============================================================================

/**
 * Represents a single cell in the ratio table
 */
export interface RatioTableCell {
  /** Unique identifier */
  id: string;
  /** Row index (0 = primary, 1 = secondary) */
  row: number;
  /** Column index */
  column: number;
  /** Value displayed in the cell */
  value: number | null;
  /** Whether this cell is visible */
  visible: boolean;
  /** Whether this cell is editable */
  editable: boolean;
  /** Linked custom tick ID (if any) */
  linkedTickId?: string;
}

/**
 * Position configuration for the ratio table
 */
export type TablePosition = 
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'custom';

/**
 * Configuration for the ratio table overlay
 */
export interface RatioTableConfig {
  /** Whether the table is visible */
  visible: boolean;
  /** Position mode */
  positionMode: TablePosition;
  /** Custom position (only used when positionMode = 'custom') */
  customPosition: { x: number; y: number };
  /** Whether table is linked to custom ticks */
  linkedToCustomTicks: boolean;
  /** Table cells */
  cells: RatioTableCell[];
  /** Number of columns */
  columnCount: number;
  /** Whether to auto-sync with custom ticks */
  autoSync: boolean;
  /** Whether table is being dragged */
  isDragging: boolean;
}

/**
 * Complete DNL Tools state
 * Nested within SecondaryLineState
 * Updated for Phase 5: Alternative Views
 * Updated for Phase 6: Context Presets
 * Updated for Phase 7: Ratio Table Overlay
 */
export interface DNLToolsState {
  /** Sparse tick mode configuration */
  sparseMode: SparseTickMode;
  /** Primary line label */
  primaryLabel: LineLabel;
  /** Secondary line label */
  secondaryLabel: LineLabel;
  /** Unit suffix for primary line */
  primaryUnit: UnitSuffix;
  /** Unit suffix for secondary line */
  secondaryUnit: UnitSuffix;
  /** Mapping arrows configuration */
  mappingArrows: MappingArrowsConfig;
  /** Alternative views configuration */
  alternativeViews: DNLAlternativeViews;
  /** Context preset state */
  contextPreset: ContextPresetState;
  /** Ratio table configuration */
  ratioTable: RatioTableConfig;
}

// =============================================================================
// ADD POINT DIALOG STATE
// =============================================================================

/**
 * State for the Add Point dialog
 */
export interface AddPointDialogState {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Which line to add the point to */
  targetLine: 'primary' | 'secondary';
  /** Pre-filled value (e.g., from click position) */
  initialValue?: number;
}

// =============================================================================
// DEFAULT VALUES
// =============================================================================

/**
 * Default sparse tick mode state
 */
export const defaultSparseTickMode: SparseTickMode = {
  enabled: false,
  hideRegularTicks: true,
  customTicks: [],
  showZero: true,
  nextId: 1,
};

/**
 * Default line label state
 */
export const defaultLineLabel: LineLabel = {
  text: '',
  subtext: undefined,
  position: 'left',
  visible: false,
};

/**
 * Default unit suffix state
 */
export const defaultUnitSuffix: UnitSuffix = {
  text: '',
  position: 'after',
  enabled: false,
};

/**
 * Default mapping arrows configuration
 */
export const defaultMappingArrowsConfig: MappingArrowsConfig = {
  enabled: false,
  arrows: [],
  defaultStyle: 'solid',
  showMultiplierLabels: false,
  defaultColour: '#3B82F6', // blue-500
};

// =============================================================================
// DEFAULT VALUES FOR PHASE 5
// =============================================================================

/**
 * Default view configuration (empty state)
 */
export const defaultViewConfiguration: ViewConfiguration = {
  primaryLabel: '',
  primarySubtext: undefined,
  secondaryLabel: '',
  secondarySubtext: undefined,
  points: [],
};

/**
 * Default alternative views state
 */
export const defaultAlternativeViews: DNLAlternativeViews = {
  currentView: 'B', // Start with View B as it's more intuitive
  viewA: { ...defaultViewConfiguration },
  viewB: { ...defaultViewConfiguration },
  animationsEnabled: false, // Opt-in for animations
  showTooltip: true,
  isConfigured: false,
};

// =============================================================================
// DEFAULT VALUES FOR PHASE 6
// =============================================================================

/**
 * Default context preset state
 */
export const defaultContextPresetState: ContextPresetState = {
  selectedPreset: 'custom',
  isPresetApplied: false,
  showDescriptions: true,
};

// =============================================================================
// DEFAULT VALUES FOR PHASE 7
// =============================================================================

/**
 * Default ratio table configuration
 */
export const defaultRatioTableConfig: RatioTableConfig = {
  visible: false,
  positionMode: 'top-right',
  customPosition: { x: 20, y: 20 },
  linkedToCustomTicks: true,
  cells: [],
  columnCount: 0,
  autoSync: true,
  isDragging: false,
};

/**
 * Default DNL tools state
 */
export const defaultDNLToolsState: DNLToolsState = {
  sparseMode: defaultSparseTickMode,
  primaryLabel: { ...defaultLineLabel },
  secondaryLabel: { ...defaultLineLabel },
  primaryUnit: { ...defaultUnitSuffix },
  secondaryUnit: { ...defaultUnitSuffix },
  mappingArrows: { ...defaultMappingArrowsConfig },
  alternativeViews: { ...defaultAlternativeViews },
  contextPreset: { ...defaultContextPresetState },
  ratioTable: { ...defaultRatioTableConfig },
};

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a tick is a DNL custom tick
 */
export function isDNLCustomTick(tick: unknown): tick is DNLCustomTick {
  return (
    typeof tick === 'object' &&
    tick !== null &&
    'id' in tick &&
    'value' in tick &&
    'line' in tick &&
    'showTick' in tick &&
    'showLabel' in tick &&
    'style' in tick
  );
}

/**
 * Check if sparse mode should render a value
 */
export function shouldRenderInSparseMode(
  value: number,
  sparseMode: SparseTickMode
): boolean {
  if (!sparseMode.enabled) return true;
  
  // Always show zero if configured
  if (sparseMode.showZero && Math.abs(value) < 1e-10) return true;
  
  // Check if value matches any custom tick
  return sparseMode.customTicks.some(
    tick => Math.abs(tick.value - value) < 1e-10 && tick.showTick
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format a value with unit suffix
 */
export function formatValueWithUnit(
  value: string | number,
  unit: UnitSuffix
): string {
  if (!unit.enabled || !unit.text) {
    return String(value);
  }
  
  return unit.position === 'before'
    ? `${unit.text}${value}`
    : `${value}${unit.text}`;
}

/**
 * Get the label for a custom tick
 */
export function getCustomTickLabel(
  tick: DNLCustomTick,
  unit: UnitSuffix
): string {
  // If tick has a custom label, use it
  if (tick.label) {
    return tick.label;
  }
  
  // Otherwise, format the value with unit
  return formatValueWithUnit(tick.value, unit);
}

/**
 * Calculate corresponding value on the other line using scale factor
 */
export function calculateCorrespondingValue(
  value: number,
  scaleFactor: Fraction,
  fromLine: 'primary' | 'secondary'
): number {
  const factor = scaleFactor.n / scaleFactor.d;
  
  return fromLine === 'primary'
    ? value * factor  // primary to secondary
    : value / factor; // secondary to primary
}

// =============================================================================
// PHASE 5: VIEW TRANSFORMATION UTILITIES
// =============================================================================

/**
 * Converts current custom ticks and labels to a ViewConfiguration
 * This captures the current state as a "view snapshot"
 */
export function captureCurrentView(
  customTicks: DNLCustomTick[],
  primaryLabel: LineLabel,
  secondaryLabel: LineLabel
): ViewConfiguration {
  return {
    primaryLabel: primaryLabel.text,
    primarySubtext: primaryLabel.subtext,
    secondaryLabel: secondaryLabel.text,
    secondarySubtext: secondaryLabel.subtext,
    points: customTicks.map(tick => ({
      id: tick.id,
      value: tick.value,
      label: tick.label,
      line: tick.line === 'both' ? 'primary' : tick.line, // Simplify 'both' to 'primary'
      style: tick.style,
      colour: tick.colour,
    })),
  };
}

/**
 * Applies a stored ViewConfiguration to current state
 * Returns the ticks and labels that should be applied
 */
export function applyViewConfiguration(
  viewConfig: ViewConfiguration
): {
  customTicks: Omit<DNLCustomTick, 'id' | 'createdAt' | 'linkedTickId'>[];
  primaryLabel: Pick<LineLabel, 'text' | 'subtext'>;
  secondaryLabel: Pick<LineLabel, 'text' | 'subtext'>;
} {
  return {
    customTicks: viewConfig.points.map(point => ({
      value: point.value,
      label: point.label,
      line: point.line,
      showTick: true,
      showLabel: point.label !== undefined,
      style: point.style,
      colour: point.colour,
    })),
    primaryLabel: {
      text: viewConfig.primaryLabel,
      subtext: viewConfig.primarySubtext,
    },
    secondaryLabel: {
      text: viewConfig.secondaryLabel,
      subtext: viewConfig.secondarySubtext,
    },
  };
}

/**
 * Checks if two views are meaningfully different
 * Used to determine if switching views will have visible effect
 */
export function areViewsDifferent(
  viewA: ViewConfiguration,
  viewB: ViewConfiguration
): boolean {
  // Check if labels differ
  const labelsDiffer = 
    viewA.primaryLabel !== viewB.primaryLabel ||
    viewA.secondaryLabel !== viewB.secondaryLabel;

  // Check if point assignments differ (same points on different lines)
  const pointsDiffer = viewA.points.length !== viewB.points.length ||
    viewA.points.some((pointA, i) => {
      const pointB = viewB.points[i];
      return !pointB || pointA.line !== pointB.line;
    });

  return labelsDiffer || pointsDiffer;
}
